import { Navigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useTenant } from '@/context/tenant-context';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  minRole?: 'admin' | 'manager' | 'support' | 'viewer';
  /**
   * Kombatix staff only — `tokenRole` super_admin / kombatix_qa.
   *
   * Deliberately NOT expressible through `minRole`. That option grades a role
   * WITHIN a tenant and hands platform admins a bypass, so its top rung
   * ('admin') is exactly what a tenant admin holds. A route that must never be
   * reachable by a customer needs the opposite test, not a higher rung.
   */
  requirePlatformAdmin?: boolean;
}

export function ProtectedRoute({
  children,
  requireAuth = true,
  minRole,
  requirePlatformAdmin,
}: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const tenant = useTenant();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (requireAuth && !user) {
    return <Navigate to="/login" replace />;
  }

  if (!requireAuth && user) {
    return <Navigate to="/" replace />;
  }

  if (requirePlatformAdmin && !(tenant?.isPlatformAdmin ?? false)) {
    return <Navigate to="/" replace />;
  }

  if (minRole && user) {
    const ROLE_RANK: Record<string, number> = { admin: 4, manager: 3, support: 2, viewer: 1 };
    const userRole = tenant?.tenantRole ?? 'viewer';
    const isPlatformAdmin = tenant?.isPlatformAdmin ?? false;
    if (!isPlatformAdmin && (ROLE_RANK[userRole] ?? 0) < (ROLE_RANK[minRole] ?? 0)) {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
}
